import { useState, useEffect } from "react";
import { useSampleEdit } from "../../../api";

export function Reachback({ sample }) {
  //state variables
  const [isReachback, setIsReachback] = useState(sample.reachback);
  const [isReachbackEmailSent, setIsReachbackEmailSent] = useState(
    sample.isReachbackEmailSent,
  );
  const [reachbackReason, setReachbackReason] = useState(
    sample.reachbackReason || {},
  );
  const [emailFailed, setEmailFailed] = useState(false);

  const { mutateAsync } = useSampleEdit(sample._id);

  // Sync server-derived fields whenever the sample data updates (refetches included)
  useEffect(() => {
    setIsReachback(sample.reachback);
    setIsReachbackEmailSent(sample.isReachbackEmailSent);
    setReachbackReason(sample.reachbackReason || {});
  }, [sample.reachback, sample.isReachbackEmailSent, sample.reachbackReason]);

  // Reset local-only UI state only when switching to a different sample
  useEffect(() => {
    setEmailFailed(false);
  }, [sample._id]);

  //when the page first loads, check if reachback is true and email has not been sent
  useEffect(() => {
    if (isReachback && !isReachbackEmailSent) {
      handleReachback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReachback]);

  //function for sending reachback email and updating isReachbackEmailSent value in database
  const sendReachbackEmail = async (emailData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/utility/send-reachback-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            sampleId: emailData.sampleId,
            processedDate: emailData.processedDate,
            reachbackReason: emailData.reachbackReason,
            notes: emailData.notes,
            isErrorT: emailData.isErrorT,
            isErrorB: emailData.isErrorB,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        try {
          await mutateAsync({
            id: sample._id,
            data: {
              isReachbackEmailSent: true,
              reachback: true,
              reachbackReason,
              reachbackComplete: "inProgress",
            },
          });
          setIsReachbackEmailSent(true);
        } catch (dbError) {
          console.error(
            "Error updating sample after reachback email sent:",
            dbError,
          );
          setIsReachback(false);
          setIsReachbackEmailSent(false);
          setEmailFailed(true);
        }
      } else {
        console.error("Error sending email:", data.error);
        setIsReachback(false);
        setIsReachbackEmailSent(false);
        setEmailFailed(true);
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setIsReachback(false);
      setIsReachbackEmailSent(false);
      setEmailFailed(true);
    }
  };

  const handleReachback = async () => {
    const emailData = {
      processedDate: sample.processedDate,
      sampleId: sample.sampleId,
      reachbackReason: reachbackReason || {},
      notes: sample.description,
      isErrorT: sample.isErrorT,
      isErrorB: sample.isErrorB,
    };

    await sendReachbackEmail(emailData);
  };

  const handleClick = () => {
    setEmailFailed(false);
    setIsReachback(true);
    setReachbackReason((prev) => ({
      ...prev,
      customerRequest: "Reachback requested by customer",
    }));
  };

  return (
    <div className='d-flex flex-row justify-content-center align-items-center flex-wrap pt-2'>
      {/* Reachback complete message */}
      {sample.reachbackComplete === "Complete" && (
        <div className='d-flex align-items-center justify-content-center mt-2 w-100 order-2'>
          <i
            className='bi bi-check-circle-fill'
            style={{
              fontSize: "large",
              color: "green",
              marginRight: "10px",
              marginBottom: "2px",
            }}
          ></i>
          <span
            style={{
              color: "black",
            }}
          >
            Reachback completed
          </span>
        </div>
      )}

      {/* Submit for Reachback button */}
      {sample.reachback === false && (
        <div className='d-flex flex-row justify-content-center align-items-center flex-wrap pt-2'>
          <h6 className=' mb-0 text-center me-2 text-wrap'>
            Would you like an expert to review your results?
          </h6>
          <div className='d-flex flex-column width-auto py-1'>
            <button
              type='submit'
              className={`btn btn-primary align-self-center  `}
              style={{ minWidth: "150px" }}
              onClick={handleClick}
              disabled={isReachback}
            >
              {isReachback
                ? "Already Submitted"
                : "Submit for Reachback Service"}
            </button>
            {isReachback && !emailFailed ? (
              <p
                className='align-self-center text-center'
                style={{
                  color: "black",
                  fontStyle: "italic",
                }}
              >
                Reachback Submitted - We'll be in touch shortly!
              </p>
            ) : emailFailed ? (
              <p
                className='align-self-center text-center'
                style={{
                  color: "#AE0000",
                  fontStyle: "italic",
                }}
              >
                Reachback submission failed. Please contact our team at{" "}
                <a href='mailto:reachback@spectraplasmonics.com'>
                  reachback@spectraplasmonics.com
                </a>{" "}
                for further assistance.
              </p>
            ) : null}
          </div>
        </div>
      )}

      {/* Reachback in progress message */}
      {(sample.reachback === true ||
        sample.reachbackComplete === "inProgress") && (
        <div className='d-flex flex-column align-items-center justify-content-center mt-2 w-100'>
          {sample.reachbackReason &&
          "modelFunctionError" in sample.reachbackReason ? (
            <div className='d-flex align-items-center'>
              <i
                className='bi bi-exclamation-octagon-fill'
                style={{
                  fontSize: "small",
                  color: "#AE0000",
                  marginRight: "10px",
                }}
              ></i>
              <span
                style={{
                  color: "#AE0000",
                  fontWeight: "bold",
                }}
              >
                <small>
                  {
                    "There was a system error while analyzing your scans. Your scans have been saved, and the Reachback team will provide further details."
                  }
                </small>
              </span>
            </div>
          ) : sample.isErrorB || sample.isErrorT ? (
            <div className='d-flex align-items-center'>
              <i
                // className='bi bi-exclamation-triangle-fill'
                className='bi-hourglass-split'
                style={{
                  fontSize: "small",
                  color: "#AE0000",
                  marginRight: "10px",
                }}
              ></i>
              <span
                style={{
                  color: "#AE0000",
                  fontWeight: "bold",
                }}
              >
                <small>
                  {`${
                    sample.isErrorB && sample.isErrorT
                      ? "Bulk scan and trace scan"
                      : sample.isErrorB
                        ? "Bulk scan"
                        : sample.isErrorT
                          ? "Trace scan"
                          : "Displayed"
                  } results are currently undergoing expert review and
                should be interpreted cautiously.`}
                </small>
              </span>
            </div>
          ) : sample.reachbackReason &&
            "modelDiscrepancy" in sample.reachbackReason ? (
            <div className='d-flex align-items-center order-last'>
              <i
                className='bi bi-search'
                style={{
                  fontSize: "14px",

                  marginRight: "6px",
                }}
              ></i>
              <span>
                <small>
                  {
                    "The initial analysis suggests that additional substances might be present in the sample. This sample will be reviewed by one of our experts."
                  }
                </small>
              </span>
            </div>
          ) : null}

          {emailFailed ? (
            <div className='d-flex align-items-center'>
              <i
                className='bi bi-exclamation-circle-fill'
                style={{
                  fontSize: "small",
                  color: "#AE0000",
                  marginRight: "10px",
                }}
              ></i>
              <span style={{ color: "#AE0000", fontWeight: "bold" }}>
                <small>
                  Reachback submission failed. Please contact our team at{" "}
                  <a href='mailto:reachback@spectraplasmonics.com'>
                    reachback@spectraplasmonics.com
                  </a>{" "}
                  for further assistance.
                </small>
              </span>
            </div>
          ) : (
            <div className='d-flex align-items-center'>
              <i
                className='bi bi-clock-fill'
                style={{
                  fontSize: "small",
                  marginRight: "10px",
                }}
              ></i>
              <span
                style={{
                  fontWeight: "bold",
                }}
              >
                <small>Reachback Submitted - We'll be in touch shortly!</small>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
